import React, { useState } from 'react'
import { Card, Button, Alert, Badge, ListGroup } from 'react-bootstrap'
import { useProvisionMunicipalityAws } from 'api/hooks/use-admin-municipality-aws-provisioning'

const STATUS_BADGE = {
  ok: { bg: 'success', label: 'OK' },
  skipped: { bg: 'light', text: 'primary', label: 'Skipped' },
  error: { bg: 'danger', label: 'Error' }
}

const StepBadge = ({ status }) => {
  const config = STATUS_BADGE[status] || { bg: 'light', text: 'primary', label: status }
  return (
    <Badge bg={config.bg} text={config.text} className="me-2">
      {config.label}
    </Badge>
  )
}

const MunicipalityAwsProvisioningPanel = ({ municipality }) => {
  const [confirmed, setConfirmed] = useState(false)
  const [result, setResult] = useState(null)

  const { mutate: provision, isLoading, error } = useProvisionMunicipalityAws({
    municipalityId: municipality.id,
    options: {
      onSuccess: (data) => setResult(data)
    }
  })

  const missingFields = []
  if (!municipality.bucket_name) missingFields.push('upload bucket')
  if (!municipality.output_bucket) missingFields.push('output bucket')
  if (!municipality.directory) missingFields.push('directory')

  const isProvisioned = municipality.bucket_name && municipality.output_bucket

  const handleProvision = () => {
    setConfirmed(false)
    setResult(null)
    provision()
  }

  return (
    <Card className="mb-4">
      <Card.Header><h5>AWS Provisioning</h5></Card.Header>
      <Card.Body>
        <p className="text-muted small mb-3">
          Creates the output S3 bucket + data folder. Access is granted via the wildcard policy
          on the <code>buckets-read-access</code> user (covers <code>*-upload</code> and{' '}
          <code>*-output</code> buckets), so no per-municipality IAM policy is needed. Safe to
          re-run — existing resources are skipped.
        </p>

        {error && (
          <Alert variant="danger" className="py-2 small">
            {error.response?.data?.message || error.message || 'Provisioning request failed'}
          </Alert>
        )}

        {isProvisioned ? (
          <dl className="row mb-0">
            <dt className="col-sm-5">Upload Bucket:</dt>
            <dd className="col-sm-7"><code>{municipality.bucket_name}</code></dd>
            <dt className="col-sm-5">Output Bucket:</dt>
            <dd className="col-sm-7"><code>{municipality.output_bucket}</code></dd>
          </dl>
        ) : missingFields.length > 0 ? (
          <Alert variant="warning" className="py-2 small mb-0">
            Missing fields needed for provisioning: {missingFields.join(', ')}. Edit the municipality first.
          </Alert>
        ) : !confirmed ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setConfirmed(true)}
            disabled={isLoading}
          >
            Provision AWS Resources
          </Button>
        ) : (
          <div>
            <Alert variant="info" className="py-2 small mb-2">
              This will hit live AWS. Continue?
            </Alert>
            <Button
              variant="primary"
              size="sm"
              onClick={handleProvision}
              disabled={isLoading}
              className="me-2"
            >
              {isLoading ? 'Provisioning…' : 'Confirm and Provision'}
            </Button>
            <Button
              variant="light"
              className="text-primary"
              size="sm"
              onClick={() => setConfirmed(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        )}

        {result && (
          <ListGroup variant="flush" className="mt-3">
            {result.steps.map((step) => (
              <ListGroup.Item key={step.name} className="px-0 py-2">
                <StepBadge status={step.status} />
                <span className="fw-semibold me-2">{step.name}</span>
                <span className="text-muted small">{step.message}</span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  )
}

export default MunicipalityAwsProvisioningPanel
